/**
 * Maintenance Mode Guard (P20250925-104)
 *
 * Wraps app and redirects non-admin users to maintenance page when MAINTENANCE_MODE=true
 */

import { ReactNode, useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { isMaintenanceMode } from '@/lib/config/flags';
import { useAuth } from '@/hooks/useAuth';

interface MaintenanceGuardProps {
  children: ReactNode;
}

export function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const location = useLocation();
  const { user, loading } = useAuth();
  const [inMaintenanceMode, setInMaintenanceMode] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  useEffect(() => {
    setInMaintenanceMode(isMaintenanceMode());
  }, []);

  useEffect(() => {
    // Check if user is admin by querying admin endpoint
    const checkAdmin = async () => {
      if (!user) {
        setIsAdminUser(false);
        return;
      }

      try {
        const response = await fetch('/functions/v1/admin-diagnostics');
        setIsAdminUser(response.ok);
      } catch {
        setIsAdminUser(false);
      }
    };

    checkAdmin();
  }, [user]);

  // Allow access if:
  // - Not in maintenance mode
  // - User is admin
  // - Route is /maintenance, /health, /ops, or /manage/*
  const isAllowedRoute =
    location.pathname === '/maintenance' ||
    location.pathname === '/health' ||
    location.pathname === '/ops' ||
    location.pathname.startsWith('/manage');

  // Wait for auth check to complete
  if (loading) {
    return <div className="min-h-screen bg-background" />;
  }

  // If in maintenance and not admin and not on allowed route, redirect
  if (inMaintenanceMode && !isAdminUser && !isAllowedRoute) {
    return <Navigate to="/maintenance" replace />;
  }

  // If on maintenance page but not actually in maintenance (or is admin), go home
  if (location.pathname === '/maintenance' && (!inMaintenanceMode || isAdminUser)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
